<template>
  <SectionTab :title="$t('reporting.industry.costs')" :description="$t('reporting.industry.costOverview')" :date="date">
    <template v-slot:section>
      <div class="order-costs" v-if="dataAvailable">
        <div class="order-costs__quick-overview">
          <div class="panel-row">
            <div class="panel -with-border -without-gap -total-costs">
              <div class="panel__inner">
                <div class="metric-row">
                  <div class="metric">
                    <span class="metric__title">
                      {{
                        $t('reporting.industry.totalCosts', {
                          month: localizedMonth,
                          year: currentYear,
                        })
                      }}</span
                    >
                    <div class="metric__numbers">
                      <span class="metric__number">{{ euro(orderCostsData.total_costs_for_month) }} </span>
                    </div>
                  </div>
                  <div class="metric">
                    <span class="metric__title">{{ $t('reporting.industry.quarterCosts') }}</span>
                    <div class="metric__numbers">
                      <span class="metric__number">{{ euro(orderCostsData.total_costs_for_quarter) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="panel__inner -white">
                <div class="metric-row">
                  <div class="metric">
                    <span class="metric__title">{{ $t('reporting.industry.monthlyDifference') }}</span>
                    <div class="metric__numbers">
                      <span class="metric__number" :class="diffClass(diffData.totalCostsToPrevMonth)"
                        >{{
                          euro(diffData.totalCostsToPrevMonth, {
                            showPlusSign: true,
                          })
                        }}
                      </span>
                    </div>
                  </div>
                  <div class="metric">
                    <span class="metric__title">{{ $t('reporting.industry.quarterlyDifference') }}</span>
                    <div class="metric__numbers">
                      <span class="metric__number" :class="diffClass(diffData.totalCostsToPrevQuarter)"
                        >{{
                          euro(diffData.totalCostsToPrevQuarter, {
                            showPlusSign: true,
                          })
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="panel -emptying-costs-data">
              <div class="panel__inner">
                <div class="metric-row">
                  <div class="metric">
                    <span class="metric__title">
                      {{ $t('reporting.industry.averageEmptyingCosts') }}
                    </span>
                    <div class="metric__numbers">
                      <span class="metric__number">{{ euro(orderCostsData.average_month_empyting_costs) }} </span>
                      <span class="metric__change" :class="diffClass(diffData.proportionalAverageEmptyingOrderCosts)">{{
                        percentage(diffData.proportionalAverageEmptyingOrderCosts, {
                          showPlusSign: true,
                        })
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="panel__inner">
                <div class="metric-row">
                  <div class="metric">
                    <span class="metric__title">
                      {{
                        $t('reporting.industry.monthlyEmptyingOrders', {
                          month: localizedMonth,
                          year: currentYear,
                        })
                      }}</span
                    >
                    <div class="metric__numbers">
                      <span class="metric__number">{{ orderCostsData.month_emptying_order_count }}</span>
                      <span class="metric__change" :class="diffClass(diffData.proportionalEmptyingOrdersCount)">{{
                        percentage(diffData.proportionalEmptyingOrdersCount, {
                          showPlusSign: true,
                        })
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="panel-row">
            <div class="panel">
              <div class="panel__inner">
                <span class="panel__title">{{ $t('meta.areas') }}</span>
                <div class="area-charts">
                  <div
                    class="area-chart"
                    v-for="[area_name, option] in Object.entries(areaCostsOptions)"
                    :key="area_name"
                  >
                    <e-chart :option="option" :autoresize="true" ref="chart"></e-chart>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <p>{{ $t('reporting.industry.noData') }}</p>
      </div>
    </template>
  </SectionTab>
</template>

<script>
import SectionTab from '@/components/Reporting/Industry/SectionTab'
import stringUtils from '@/components/mixins/stringUtils'

export default {
  name: 'OrderCosts',
  props: {
    orderCostsData: {
      type: Object,
      required: true,
    },
    date: { type: String, default: '' },
  },
  components: { SectionTab },
  mixins: [stringUtils],
  data() {
    return {
      areaCostsOptions: {},
      diffData: {
        proportionalEmptyingOrdersCount: 0,
        proportionalAverageEmptyingOrderCosts: 0,
        totalCostsToPrevMonth: 0,
        totalCostsToPrevQuarter: 0,
      },
      dataAvailable: true,
    }
  },
  watch: {
    orderCostsData() {
      this.calculateDiffData()
      this.buildAreaCostsChartOptions()
    },
  },
  computed: {
    localizedMonth: function () {
      const now = new Date()
      return now.toLocaleString(this.$i18n.locale, { month: 'long' })
    },
    currentYear: function () {
      const now = new Date()
      return now.getFullYear()
    },
  },
  methods: {
    calculateDiffData() {
      let proportionalEmptyingOrdersCount = 0
      if (this.orderCostsData.previous_month_emptying_order_count > 0) {
        proportionalEmptyingOrdersCount =
          (this.orderCostsData.month_emptying_order_count - this.orderCostsData.previous_month_emptying_order_count) /
          this.orderCostsData.previous_month_emptying_order_count
      }

      let proportionalAverageEmptyingOrderCosts = 0
      if (this.orderCostsData.average_previous_month_empyting_costs > 0) {
        proportionalAverageEmptyingOrderCosts =
          (this.orderCostsData.average_month_empyting_costs -
            this.orderCostsData.average_previous_month_empyting_costs) /
          this.orderCostsData.average_previous_month_empyting_costs
      }

      const totalCostsToPrevMonth =
        this.orderCostsData.total_costs_for_month - this.orderCostsData.total_costs_for_previous_month
      const totalCostsToPrevQuarter =
        this.orderCostsData.total_costs_for_quarter - this.orderCostsData.total_costs_for_previous_quarter

      this.diffData = {
        proportionalEmptyingOrdersCount: proportionalEmptyingOrdersCount.toFixed(2),
        proportionalAverageEmptyingOrderCosts: proportionalAverageEmptyingOrderCosts.toFixed(2),
        totalCostsToPrevMonth: totalCostsToPrevMonth,
        totalCostsToPrevQuarter: totalCostsToPrevQuarter,
      }
    },
    diffClass(number) {
      return number >= 0 ? '-increase' : '-decrease'
    },
    buildAreaCostsChartOptions() {
      let chartOptions = {}

      const previousMonthTotalCosts = this.orderCostsData.previous_month_costs_by_area
      this.orderCostsData.month_costs_by_area?.forEach(entry => {
        const previousMonthCostsForEntry = previousMonthTotalCosts.find(
          e => e.area_name === entry.area_name,
        )?.total_area_costs
        const valueDiffToPreviousMonth = entry.total_area_costs - previousMonthCostsForEntry
        const proportionalChange = parseFloat((valueDiffToPreviousMonth / previousMonthCostsForEntry).toFixed(2))
        const hasIncreased = proportionalChange > 0

        let chartOption = {
          title: {
            text: [
              `{totalCosts|${this.euro(entry.total_area_costs)}}`,
              `{${hasIncreased ? 'increase' : 'decrease'}|${this.euro(valueDiffToPreviousMonth, {
                showPlusSign: true,
              })}}`,
            ].join('\n'),
            subtext: entry.area_name,
            textStyle: {
              rich: {
                totalCosts: {
                  fontSize: 16,
                  fontWeight: 'bold',
                  color: '#000',
                },
                increase: {
                  color: '#138084',
                  fontSize: 13,
                  fontWeight: 'bold',
                  lineHeight: 16,
                },
                decrease: {
                  color: '#92AA00',
                  fontSize: 13,
                  fontWeight: 'bold',
                  lineHeight: 16,
                },
              },
            },
            subtextStyle: {
              fontSize: 14,
              fontWeight: 400,
              color: '#000',
            },
            left: 'center',
            bottom: 50,
          },
          series: [],
          tooltip: {
            trigger: 'item',
            position: 'left',
            show: true,
            valueFormatter: value => `${this.euro(parseFloat(value).toFixed(2))}`,
          },
        }

        let basicChartProperties = {
          startAngle: 180,
          endAngle: 360,
          type: 'pie',
          radius: ['50', '65'],
          avoidLabelOverlap: false,
          color: ['#5C5D63', '#4F8EDE', '#015390', '#AEC90A', '#FAB700', '#E2001A', '#30B2B7', '#138084'],
          label: {
            show: false,
          },
          emphasis: {
            scale: true,
          },
        }

        let hiddenPieEntryValue = entry.total_area_costs
        // In case the total costs decreased, we need to shrink the visible pie data.
        // This can be done by making the invisible part of the pie bigger.
        if (valueDiffToPreviousMonth < 0) {
          // Subtract because the value is negative, so we add it.
          hiddenPieEntryValue -= valueDiffToPreviousMonth
        }

        // Prepare chart data per zone
        let zoneData = []
        this.orderCostsData.month_costs_by_zone
          .filter(zData => zData.area_name === entry.area_name)
          .forEach(zData => {
            zoneData.push({
              name: zData.zone_name,
              value: zData.total_zone_costs,
            })
          })

        let dataChartOption = {
          ...basicChartProperties,
          data: [
            ...zoneData,
            {
              name: null,
              value: hiddenPieEntryValue,
              // stop the chart from rendering this piece
              itemStyle: {
                color: 'none',
              },
              label: {
                show: false,
              },
              emphasis: {
                disabled: true,
              },
            },
          ],
        }
        chartOption.series.push(dataChartOption)

        const clampedProportionalChange = Math.min(proportionalChange, 1)
        if (hasIncreased) {
          let diffChartOption = {
            type: 'pie',
            radius: ['45', '51'],
            startAngle: 180,
            endAngle: 360,
            label: {
              show: false,
            },
            labelLine: {
              show: false,
            },
            emphasis: {
              scale: false,
            },
            data: [
              {
                value: clampedProportionalChange,
                itemStyle: {
                  color: '#138084',
                },
              },
              {
                value: 2 - clampedProportionalChange,
                itemStyle: {
                  opacity: 0,
                },
              },
            ],
          }

          chartOption.series.push(diffChartOption)
        }

        chartOptions[entry.area_name] = chartOption
      })

      this.areaCostsOptions = chartOptions
    },
  },
  mounted() {
    this.calculateDiffData()
    this.buildAreaCostsChartOptions()
  },
}
</script>

<style lang="scss" scoped>
.order-costs {
  margin: 1rem;
}

.area-charts {
  display: flex;
  flex-wrap: wrap;
}

.area-chart {
  width: 150px;
  height: 200px;
}

.panel {
  display: flex;
  flex-direction: column;
  border-radius: var(--border-radius-m);
  justify-content: space-between;
  flex: 0 1 100%;

  &__title {
    display: inline-block;
    text-align: center;
    font-size: 16px;
    font-weight: bold;
    line-height: 32px;
    width: 100%;
  }

  &-row {
    display: flex;
    column-gap: 10px;
    margin-top: 20px;
  }

  &.-with-border {
    border: 3px solid #d8d8d8;
    .panel__inner {
      border-radius: 0;
    }
  }

  &.-without-gap {
    row-gap: 0;
  }

  &__inner {
    background-color: #f4f4f4;
    padding: 10px 3%;
    border-radius: var(--border-radius-m);

    &.-white {
      background-color: white;
    }
  }

  &.-total-costs {
    flex-basis: 60%;
  }

  &.-emptying-costs-data {
    flex-basis: 40%;
  }
}

.metric {
  &-row {
    display: flex;
    column-gap: 10px;
    align-items: center;
    justify-content: space-around;
  }

  &__title {
    display: inline-block;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 8px;
    text-align: center;
  }

  &__numbers {
    display: flex;
    justify-content: space-around;
    align-items: center;
    column-gap: 20px;
  }

  &__number {
    display: inline-block;
    font-size: 24px;
    font-weight: bold;
  }

  &__change {
    .metric__number + & {
      display: inline-block;
      font-size: 15px;
    }
  }

  .-increase {
    color: #138084;
  }

  .-decrease {
    color: #92aa00;
  }
}
</style>
